import { Link } from '@inertiajs/react'
import { Calendar } from 'lucide-react'
import type { RecentMeeting, MeetingStatus } from '../../types'

interface Props {
  meetings: RecentMeeting[]
  teamId: number | null
  agendaName: string | null
}

const STATUS_STYLES: Record<MeetingStatus, { label: string; badgeClass: string }> = {
  completed: { label: 'Completed', badgeClass: 'text-status-win bg-status-win/10' },
  in_progress: { label: 'In Progress', badgeClass: 'text-status-warning bg-status-warning/10' },
  scheduled: { label: 'Scheduled', badgeClass: 'text-text-muted bg-[#5F6368]/10' },
  cancelled: { label: 'Cancelled', badgeClass: 'text-status-miss bg-status-miss/10' },
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'No date'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function RecentMeetings({ meetings, teamId, agendaName }: Props) {
  const startLabel = agendaName ? `Start ${agendaName}` : 'Start Meeting'
  if (meetings.length === 0) {
    return (
      <section>
        <h2 className="text-base font-bold uppercase tracking-widest text-text-muted mb-4 font-sans">
          RECENT MEETINGS
        </h2>
        <div className="bg-white rounded-xl shadow-card border border-stone/10 p-8 text-center">
          <Calendar className="w-12 h-12 text-text-muted mx-auto mb-3" />
          <p className="text-xl font-semibold text-text-primary mb-2 font-sans">No meetings yet</p>
          <p className="text-lg text-text-muted mb-4">Start your first weekly tactical meeting to build momentum.</p>
          {teamId ? (
            <Link
              href={`/teams/${teamId}/start_meeting`}
              method="post"
              as="button"
              className="inline-block bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold py-2.5 px-6 rounded-lg transition-colors text-base font-sans"
            >
              {startLabel}
            </Link>
          ) : (
            <Link
              href="/teams"
              className="inline-block bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold py-2.5 px-6 rounded-lg transition-colors text-base font-sans"
            >
              Join a Team
            </Link>
          )}
        </div>
      </section>
    )
  }

  return (
    <section>
      <h2 className="text-base font-bold uppercase tracking-widest text-text-muted mb-4 font-sans">
        RECENT MEETINGS
      </h2>
      <div className="bg-white rounded-xl shadow-card border border-stone/10 divide-y divide-stone/10">
        {meetings.map((meeting) => {
          const style = STATUS_STYLES[meeting.status]
          return (
            <Link
              key={meeting.id}
              href={`/meetings/${meeting.id}`}
              className="flex items-center justify-between px-5 py-4 hover:bg-[#F8F9FA] transition-colors first:rounded-t-xl last:rounded-b-xl"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Calendar className="w-6 h-6 text-text-muted flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-lg font-semibold text-text-primary truncate font-sans">{meeting.name}</p>
                  <p className="text-sm text-text-muted">{formatDate(meeting.scheduled_date)}</p>
                </div>
              </div>
              <span
                className={`text-sm font-bold uppercase tracking-wide px-2.5 py-1 rounded-full flex-shrink-0 ml-3 ${style.badgeClass}`}
              >
                {style.label}
              </span>
            </Link>
          )
        })}
      </div>
    </section>
  )
}
